'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { GlobeIcon } from '@/components/shared/icons';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getAdminDashboardCardIcon } from '@/components/admin/admin-dashboard-card-icons';
import { useAdminNavbarPins } from '@/hooks/use-admin-navbar-pins';
import { adminDashboardCardLinkProps, isExternalAdminCardHref } from '@/lib/admin/dashboard-cards-registry';
import { resolveAdminNavbarPinCards } from '@/lib/admin/navbar-pins';
import { isSuperAdminViewClient } from '@/lib/permissions/client';
import { useRTLUtils, useLanguage } from '@/lib/translation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type SuperAdminNavShortcutsTheme = 'product' | 'admin';

function isShortcutActive(pathname: string, href: string): boolean {
  if (isExternalAdminCardHref(href)) return false;
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function shortcutLinkClass(theme: SuperAdminNavShortcutsTheme, active: boolean): string {
  return cn(
    buttonVariants({ variant: 'outline', size: 'sm' }),
    'hidden md:inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border bg-transparent p-0 shadow-none',
    theme === 'product'
      ? cn(
          'text-yellow-700 hover:text-yellow-800 border-yellow-200 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:text-yellow-200 dark:border-yellow-900/50 dark:hover:bg-yellow-900/20',
          active && 'bg-yellow-50 dark:bg-yellow-900/20',
        )
      : cn(
          'text-orange-700 hover:text-orange-800 border-orange-200 hover:bg-orange-100 dark:text-orange-300 dark:hover:text-orange-200 dark:border-orange-900/50 dark:hover:bg-orange-900/20',
          active && 'bg-orange-50 dark:bg-orange-900/20',
        ),
  );
}

function languageIndicatorClass(theme: SuperAdminNavShortcutsTheme): string {
  return theme === 'product' ? 'bg-primary' : 'bg-orange-500';
}

type SuperAdminNavShortcutsProps = {
  theme: SuperAdminNavShortcutsTheme;
  /** When false, only the language (globe) control is shown — e.g. localhost dev on the main navbar. */
  showAdminLinks?: boolean;
};

/**
 * Fixed language globe + up to 5 per-user admin tool pins.
 * Shell layout toggle lives in System Preferences (not here).
 */
export function SuperAdminNavShortcuts({
  theme,
  showAdminLinks = true,
}: SuperAdminNavShortcutsProps) {
  const pathname = usePathname();
  const { t } = useTranslation();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { orgRole, sessionClaims } = useAuth();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const { pins } = useAdminNavbarPins(showAdminLinks && isSuperAdmin);
  const pinCards = resolveAdminNavbarPinCards(pins);

  return (
    <div dir="ltr" className="flex items-center gap-2">
      {showAdminLinks && isSuperAdmin
        ? pinCards.map((card) => {
            const href = card.href!;
            const Icon = getAdminDashboardCardIcon(card);
            const label = t(card.titleKey, card.titleDefault);
            return (
              <TooltipProvider key={card.id} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      {...adminDashboardCardLinkProps(href)}
                      aria-label={label}
                      className={shortcutLinkClass(
                        theme,
                        isShortcutActive(pathname ?? '', href),
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })
        : null}

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={shortcutLinkClass(theme, false)}
            aria-label={t('language.switchLanguage', 'Switch Language')}
          >
            <GlobeIcon size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="min-w-[140px]">
          {supportedLanguages.map((language) => (
            <DropdownMenuItem
              key={language.code}
              onClick={() => {
                changeLanguage(language.code);
                if (typeof window !== 'undefined') {
                  window.location.reload();
                }
              }}
              className={cn('cursor-pointer', directionClass)}
            >
              <span className={textAlignClass}>{language.name}</span>
              {currentLanguage.code === language.code && (
                <div
                  className={cn(
                    'ml-auto h-2 w-2 rounded-full',
                    languageIndicatorClass(theme),
                    isRTL && 'ml-0 mr-auto',
                  )}
                />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
